// lib/services/helperService.js
import axios from 'axios';
import { forEach, keysIn, sortBy, groupBy, map, mapValues, chain } from 'lodash';
import useSWR from 'swr'


export const API_METHODS = {
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT',
  DELETE: 'DELETE'
};

export class HelperService {
  constructor() {
    this.loading = false;
    
    // Lodash utilities
    this.iterations = forEach;
    this.sortBy = sortBy;
    this.groupBy = groupBy;
    this.map = map;
    this.keysIn = keysIn;
    this.mapValues = mapValues;
    this.chain = chain;

    this.axiosInstance = axios.create({
      baseURL: process.env.NEXT_PUBLIC_API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add interceptors
    this.axiosInstance.interceptors.request.use(
      (config) => {
        this.loading = true;
        return config;
      },
      (error) => {
        this.loading = false;
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        this.loading = false;
        return response;
      },
      (error) => {
        this.loading = false;
        return Promise.reject(error);
      }
    );
  }

  /**
   * Generic fetcher function for SWR
   * @param {string} url - API endpoint
   */
  async fetcher(url) {
    try {
      const response = await this.axiosInstance.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Generic mutation function
   * @param {string} url - API endpoint
   * @param {object} data - Request payload
   * @param {string} method - HTTP method
   */
  async CRUD(url, data, method = API_METHODS.POST) {
    try {
      let response;
      
      switch (method) {
        case API_METHODS.POST:
          response = await this.axiosInstance.post(url, data);
          break;
        case API_METHODS.PUT:
          response = await this.axiosInstance.put(url, data);
          break;
        case API_METHODS.DELETE:
          response = await this.axiosInstance.delete(url);
          break;
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      // Revalidate the cache for this URL
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  handleError(error) {
    const errorResponse = {
      message: error.response?.data?.message || error.message || 'An unexpected error occurred',
      status: error.response?.status || 500
    };

    if (error.response) {
      console.log(
        `Backend returned code ${error.response.status}, `,
        `body was: ${JSON.stringify(error.response.data)}`
      );
    } else if (error.request) {
      console.error('No response received:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }

    throw errorResponse;
  }
}

// Create singleton instance
export const helperService = new HelperService();

// Custom hooks for data fetching
export function useData(url, options = {}) {
  return useSWR(url, helperService.fetcher.bind(helperService), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    ...options
  });
}

// Custom hook for mutations
export function useMutation(url) {
  const updateData = async (data, method = API_METHODS.POST) => {
    try {
      return await helperService.CRUD(url, data, method);
    } catch (error) {
      throw error;
    }
  };

  return {
    updateData,
    loading: helperService.loading
  };
}